<template>
  <el-table
    ref="tableRef"
    :default-sort="defaultSort"
    :data="data"
    :row-key="rowKey"
    style="width: 100%; font-size: var(--tea-component-table-font-size)"
    v-loading="loading"
    :show-summary="showSummary"
    :row-class-name="rowClassName"
    :summary-method="getSummaries"
    @sort-change="sortChange"
    @expand-change="onExpandChange"
    @row-click="onRowClick"
    @selection-change="onSelectionChange"
    :empty-text="emptyText"
    :border="border"
    :stripe="stripe"
    :size="size"
    :expand-row-keys="expandRowKeys"
    @row-dblclick="onRowDblClick"
  >
    <el-table-column
      v-if="selection"
      type="selection"
      width="55"
      align="center"
      :selectable="selectable"
    />
    <el-table-column
      v-for="(column, index) in columns"
      :key="index"
      :prop="String(column.prop)"
      :label="column.label"
      :min-width="column.minWidth"
      :width="column.width"
      :sortable="column.sortable"
      :align="column.align"
      :type="column.type"
      :index="column.index"
      :show-overflow-tooltip="column.showOverflowTooltip"
      :className="column.class"
      :fixed="column.fixed"
    >
      <template v-if="column.headerCellRenderer" #header>
        <!-- TODO 优化，修改 传参 -->
        <component :is="column.headerCellRenderer()" />
      </template>
      <template v-if="column.cellRenderer" #default="scope">
        <span :class="hasError(scope) ? 'table-item__error' : ''">
          <component :is="column.cellRenderer(scope.row, scope)" />
          <div v-if="hasError(scope)" class="table-item__error-label">
            {{ scope.row.errorMessages?.[scope.cellIndex].map((err: any) => err.message).join(", ") }}
          </div>
        </span>
      </template>
    </el-table-column>
    <slot />
  </el-table>
</template>

<script lang="ts" setup>
import type { ColumnCls, ElTable, FormRules, Sort, SummaryMethod } from "element-plus"
import type { PropType } from "vue"
import type { BaseTableColumns } from "@/components/table/types"
import { ref, toRefs } from "vue"
import { validateArray } from "@/utils/validate"

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  columns: {
    type: Array as PropType<BaseTableColumns<any>>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  sortChange: {
    type: Function as PropType<(..._args: any[]) => void>,
  },
  getSummaries: {
    type: Function as PropType<SummaryMethod<any>>,
  },
  rowClassName: {
    type: Object as PropType<ColumnCls<any> | undefined>,
  },
  showSummary: {
    type: Boolean,
  },
  rowKey: {
    type: String,
  },
  defaultSort: {
    type: Object as PropType<Sort>,
  },
  tableRules: {
    type: Object as PropType<FormRules>,
    default: () => ({}),
  },
  emptyText: {
    type: String,
    default: "暂无数据",
  },
  border: {
    type: Boolean,
    default: false,
  },
  stripe: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<'' | 'large' | 'default' | 'small'>,
    default: '' as '' | 'large' | 'default' | 'small',
  },
  expandRowKeys: {
    type: Array as PropType<string[]>,
  },
  selection: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Function as PropType<(_row: any, _index: number) => boolean>,
    default: undefined,
  },
})

const { data, columns, loading, tableRules, expandRowKeys } = toRefs(props)
const tableRef = ref<InstanceType<typeof ElTable>>()
const hasError = (scope: any) => {
  return scope.row.errorMessages?.[scope.cellIndex]?.length > 0
}

const emit = defineEmits(['row-click', 'row-dblclick', 'expand-change', 'selection-change'])

function onRowClick(_row: any, _column: any, _event: MouseEvent) {
  emit('row-click', _row, _column, _event)
}

function onRowDblClick(_row: any, _column: any, _event: MouseEvent) {
  emit('row-dblclick', _row, _column, _event)
}

function onExpandChange(_row: any, _expanded: boolean) {
  emit('expand-change', _row, _expanded)
}

function onSelectionChange(selection: any[]) {
  emit('selection-change', selection)
}

const clearSort = () => {
  tableRef.value?.clearSort()
}

const clearSelection = () => {
  tableRef.value?.clearSelection()
}

const validate = () => {
  return validateArray(tableRules.value, data?.value || [], columns.value)
}

const setScrollLeft = (number?: number) => {
  tableRef.value?.setScrollLeft(number)
}

defineExpose({
  clearSort,
  clearSelection,
  validate,
  setScrollLeft,

  // 暴露el-table的ref，让父组件可以直接访问
  tableRef,

  // 通过代理暴露el-table的所有方法和属性
  get elTable() {
    return tableRef.value
  },
})
</script>

<style scoped lang="scss">
.table-item__error {
  &:deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px var(--el-color-danger) inset;
  }

  .table-item__error-label {
    padding-top: var(--tea-spacing-3xs);
    font-size: var(--tea-font-size-caption);
    line-height: 1;
    color: var(--el-color-danger);
  }
}
</style>
